import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
    Box,
    Button,
    CircularProgress,
    FormControlLabel,
    FormHelperText,
    Grid,
    Link,
    Paper,
    Switch,
    TextField,
    Typography,
    useMediaQuery,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { ButtonContainer, MOBILE_STICKY_BOTTOM_INSET_PX } from '../../components/ManageCollection/ManageCollection.styles';
import { routes } from '../../constants.js/misc';
import {
    CART_SUBTOTAL_FEATURE_NAME,
    CART_SUBTOTAL_FEATURE_TITLE,
    isPromotionsFeatureEnabled,
    promotionsPageUrl,
    PROMOTIONS_TAB_CART_SUBTOTAL,
} from '../../constants/promotionConfig';
import {
    buildCartDiscountTierPayload,
    cartRowsToThresholds,
    createEmptyCartThreshold,
    thresholdsToCartRows,
    validateCartDiscountTierForm,
} from '../../constants/cartDiscountTierConfig';
import {
    getCartDiscountTierService,
    parseCartDiscountTierFromResponse,
    upsertCartDiscountTierService,
} from '../../services/cartDiscountTier';
import CartDiscountTierThresholdsSection from './components/CartDiscountTierThresholdsSection';
import PromotionsBestOfNotice from './components/PromotionsBestOfNotice';
import CartDiscountTierSidebar, {
    CartDiscountTierStatusPanel,
} from './components/CartDiscountTierSidebar';
import LimitedFreemiumPlan from '../../components/LimitedFreemiumPlan';

const emptyForm = {
    name: '',
    discount_type: 'percentage',
    active: true,
    useSchedule: false,
    starts_at: '',
    ends_at: '',
    thresholds: [createEmptyCartThreshold()],
};

const toDateInputValue = (iso) => {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (value) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const toIsoStart = (dateStr) => {
    if (!dateStr) return null;
    return new Date(`${dateStr}T00:00:00`).toISOString();
};

const toIsoEnd = (dateStr) => {
    if (!dateStr) return null;
    return new Date(`${dateStr}T23:59:59`).toISOString();
};

const SectionCard = ({ title, subtitle, children, action, compact }) => (
    <Paper
        elevation={0}
        style={{
            padding: compact ? 14 : 20,
            marginBottom: compact ? 12 : 16,
            borderRadius: 12,
            border: '1px solid #E8E8E8',
        }}
    >
        <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={subtitle ? (compact ? 0.75 : 1) : compact ? 1.5 : 2}
        >
            <Box pr={action ? 1 : 0}>
                <Typography
                    variant={compact ? 'body1' : 'subtitle1'}
                    style={{ fontWeight: 600, fontSize: compact ? 15 : undefined }}
                >
                    {title}
                </Typography>
                {subtitle && (
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ marginTop: 4, lineHeight: 1.45, fontSize: compact ? 13 : undefined }}
                    >
                        {subtitle}
                    </Typography>
                )}
            </Box>
            {action}
        </Box>
        {children}
    </Paper>
);

const CartDiscountTierFormPage = ({ storeId, storeData }) => {
    const history = useHistory();
    const location = useLocation();
    const isEdit = location.pathname.includes('/cart-tier/edit');
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [form, setForm] = useState(emptyForm);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);

    const storeCurrency = storeData?.currency || storeData?.store_plan?.currency;
    const promotionsFeatureEnabled = isPromotionsFeatureEnabled(storeData);
    const backUrl = promotionsPageUrl(PROMOTIONS_TAB_CART_SUBTOTAL);

    const patchForm = (patch) => setForm((prev) => ({ ...prev, ...patch }));

    const applyRuleToForm = useCallback((data) => {
        setForm({
            ...emptyForm,
            name: data.name || '',
            discount_type: data.discount_type || 'percentage',
            active: data.active !== false,
            useSchedule: Boolean(data.starts_at || data.ends_at),
            starts_at: toDateInputValue(data.starts_at),
            ends_at: toDateInputValue(data.ends_at),
            thresholds: thresholdsToCartRows(data.thresholds),
        });
    }, []);

    useEffect(() => {
        if (!promotionsFeatureEnabled || !storeId) return;

        if (isEdit) {
            setLoading(true);
            getCartDiscountTierService(storeId)
                .then(({ data }) => {
                    const rule = parseCartDiscountTierFromResponse(data);
                    if (!rule) {
                        throw new Error('NO_RULE');
                    }
                    applyRuleToForm(rule);
                })
                .catch(() => {
                    enqueueSnackbar(`No se pudo cargar el ${CART_SUBTOTAL_FEATURE_NAME}`, {
                        variant: 'error',
                        autoHideDuration: 3000,
                    });
                    history.push(backUrl);
                })
                .finally(() => setLoading(false));
            return;
        }

        getCartDiscountTierService(storeId)
            .then(({ data }) => {
                const rule = parseCartDiscountTierFromResponse(data);
                if (rule) {
                    history.replace(routes.promotionsCartTierEdit);
                }
            })
            .catch(() => {});
    }, [
        isEdit,
        storeId,
        promotionsFeatureEnabled,
        enqueueSnackbar,
        history,
        backUrl,
        applyRuleToForm,
    ]);

    const handleThresholdChange = (localId, patch) => {
        patchForm({
            thresholds: form.thresholds.map((row) =>
                row.localId === localId ? { ...row, ...patch } : row
            ),
        });
    };

    const handleAddThreshold = () => {
        setForm((prev) => {
            if (prev.thresholds.length >= 10) return prev;
            return {
                ...prev,
                thresholds: [...prev.thresholds, createEmptyCartThreshold()],
            };
        });
    };

    const handleRemoveThreshold = (localId) => {
        setForm((prev) => {
            if (prev.thresholds.length <= 1) return prev;
            return {
                ...prev,
                thresholds: prev.thresholds.filter((row) => row.localId !== localId),
            };
        });
    };

    const formValidation = useMemo(
        () => validateCartDiscountTierForm(form.thresholds, form.discount_type),
        [form.thresholds, form.discount_type]
    );

    const validateForm = () => {
        if (!(form.name || '').trim()) {
            enqueueSnackbar('Ingresa un nombre para la regla', { variant: 'warning' });
            return false;
        }
        if (!formValidation.valid) {
            enqueueSnackbar(formValidation.message, { variant: 'warning' });
            return false;
        }
        if (cartRowsToThresholds(form.thresholds).length === 0) {
            enqueueSnackbar('Agrega al menos un nivel de descuento', { variant: 'warning' });
            return false;
        }
        if (form.useSchedule) {
            if (!form.starts_at || !form.ends_at) {
                enqueueSnackbar('Completa fecha de inicio y fin', { variant: 'warning' });
                return false;
            }
            if (form.starts_at > form.ends_at) {
                enqueueSnackbar('La fecha de fin debe ser posterior al inicio', { variant: 'warning' });
                return false;
            }
        }
        return true;
    };

    const handleSave = async () => {
        if (!storeId || !validateForm()) return;

        const payload = buildCartDiscountTierPayload({
            name: form.name,
            discount_type: form.discount_type,
            thresholds: cartRowsToThresholds(form.thresholds),
            active: form.active,
            useSchedule: form.useSchedule,
            starts_at: form.useSchedule ? toIsoStart(form.starts_at) : null,
            ends_at: form.useSchedule ? toIsoEnd(form.ends_at) : null,
        });

        setSaving(true);
        try {
            await upsertCartDiscountTierService(storeId, payload);
            enqueueSnackbar(
                isEdit
                    ? `${CART_SUBTOTAL_FEATURE_TITLE} actualizado`
                    : `${CART_SUBTOTAL_FEATURE_TITLE} creado`,
                { variant: 'success', autoHideDuration: 2500 }
            );
            history.push(backUrl);
        } catch {
            enqueueSnackbar(`No se pudo guardar el ${CART_SUBTOTAL_FEATURE_NAME}`, {
                variant: 'error',
                autoHideDuration: 3000,
            });
        } finally {
            setSaving(false);
        }
    };

    const sectionCompact = isMobile;

    const formColumn = (
        <>
            <PromotionsBestOfNotice compact={sectionCompact} />
            <SectionCard title="Información general" compact={sectionCompact}>
                <TextField
                    fullWidth
                    variant="outlined"
                    label="Nombre de la regla"
                    value={form.name}
                    onChange={(e) => patchForm({ name: e.target.value })}
                    placeholder="Ej. Descuento por compras grandes"
                />
            </SectionCard>

            <SectionCard
                title="Niveles de descuento"
                compact={sectionCompact}
                action={
                    isMobile ? null : (
                        <Button
                            size="small"
                            color="primary"
                            onClick={handleAddThreshold}
                            disabled={form.thresholds.length >= 10}
                            style={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                        >
                            + Agregar nivel
                        </Button>
                    )
                }
            >
                <CartDiscountTierThresholdsSection
                    thresholds={form.thresholds}
                    discountType={form.discount_type}
                    storeCurrency={storeCurrency}
                    onDiscountTypeChange={(discountType) => patchForm({ discount_type: discountType })}
                    onThresholdChange={handleThresholdChange}
                    onRemoveThreshold={handleRemoveThreshold}
                    onAddThreshold={isMobile ? handleAddThreshold : undefined}
                    canAddThreshold={form.thresholds.length < 10}
                />
            </SectionCard>

            <SectionCard
                title="Vigencia"
                subtitle={
                    isMobile
                        ? 'Opcional: fechas de inicio y fin.'
                        : `Opcional: limita cuándo aplica el ${CART_SUBTOTAL_FEATURE_NAME}.`
                }
                compact={sectionCompact}
                action={
                    <FormControlLabel
                        control={
                            <Switch
                                color="primary"
                                checked={form.useSchedule}
                                onChange={(e) => patchForm({ useSchedule: e.target.checked })}
                            />
                        }
                        label="Definir fechas"
                        labelPlacement="start"
                        style={{ marginRight: 0 }}
                    />
                }
            >
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="Fecha de inicio"
                            type="date"
                            value={form.starts_at}
                            onChange={(e) => patchForm({ starts_at: e.target.value })}
                            disabled={!form.useSchedule}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="Fecha de fin"
                            type="date"
                            value={form.ends_at}
                            onChange={(e) => patchForm({ ends_at: e.target.value })}
                            disabled={!form.useSchedule}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>
                {form.useSchedule && !isMobile && (
                    <FormHelperText>
                        El descuento solo está activo entre la fecha de inicio y la de fin.
                    </FormHelperText>
                )}
            </SectionCard>

            {isMobile && (
                <CartDiscountTierSidebar
                    form={form}
                    storeCurrency={storeCurrency}
                    isEdit={isEdit}
                    isMobile
                    onActiveChange={(active) => patchForm({ active })}
                />
            )}
        </>
    );

    if (!promotionsFeatureEnabled) {
        return (
            <Box display="flex" justifyContent="center" width="100%" p={2} pb={isMobile ? 10 : 2}>
                <Box maxWidth={560} width="100%">
                    <LimitedFreemiumPlan type="payment" embedded />
                </Box>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
                <CircularProgress />
            </Box>
        );
    }

    const pageTitle = isEdit
        ? `Editar ${CART_SUBTOTAL_FEATURE_NAME}`
        : `Nuevo ${CART_SUBTOTAL_FEATURE_NAME}`;

    return (
        <Box pb={isMobile ? 0 : 4} style={{ paddingBottom: isMobile ? MOBILE_STICKY_BOTTOM_INSET_PX : undefined }}>
            <Grid container spacing={isMobile ? 0 : 3}>
                <Grid item xs={12} style={{ padding: isMobile ? '12px 16px 8px' : '0 0 20px' }}>
                    {isMobile ? (
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography variant="h6" style={{ fontWeight: 600, fontSize: 18, color: '#1E1E1E' }}>
                                {pageTitle}
                            </Typography>
                            <CartDiscountTierStatusPanel
                                active={form.active}
                                onActiveChange={(active) => patchForm({ active })}
                                isMobile
                                inline
                            />
                        </Box>
                    ) : (
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap">
                            <Box>
                                <Typography variant="h4" style={{ marginBottom: 10 }}>
                                    {pageTitle}
                                </Typography>
                                <Link component={RouterLink} to={backUrl}>
                                    {t('PromotionsTitle')}
                                </Link>
                            </Box>
                            <Box display="flex" alignItems="center" style={{ marginTop: 8 }}>
                                <Button size="small" component={RouterLink} to={backUrl}>
                                    Cancelar
                                </Button>
                                <Button
                                    color="primary"
                                    variant="contained"
                                    style={{ marginLeft: 16 }}
                                    onClick={handleSave}
                                    disabled={saving || !formValidation.valid}
                                >
                                    {saving ? 'Guardando…' : 'Guardar regla'}
                                </Button>
                            </Box>
                        </Box>
                    )}
                    {isMobile && (
                        <Link
                            component={RouterLink}
                            to={backUrl}
                            style={{ fontSize: 14, display: 'inline-block', marginTop: 8 }}
                        >
                            ‹ {t('PromotionsTitle')}
                        </Link>
                    )}
                </Grid>

                <Grid
                    container
                    spacing={isMobile ? 0 : 3}
                    style={isMobile ? { backgroundColor: '#f5f5f5', paddingBottom: 24 } : undefined}
                >
                    <Grid item xs={12} lg={8}>
                        <Box style={isMobile ? { padding: '0 12px' } : undefined}>{formColumn}</Box>
                    </Grid>

                    {!isMobile && (
                        <Grid item xs={12} lg={4}>
                            <CartDiscountTierSidebar
                                form={form}
                                storeCurrency={storeCurrency}
                                isEdit={isEdit}
                                isMobile={false}
                                onActiveChange={(active) => patchForm({ active })}
                            />
                        </Grid>
                    )}

                    {isMobile && (
                        <ButtonContainer item xs={12}>
                            <Button
                                style={{ width: '100%', padding: '12px 0', fontWeight: 600, fontSize: 15 }}
                                color="primary"
                                variant="contained"
                                onClick={handleSave}
                                disabled={saving || !formValidation.valid}
                            >
                                {saving ? 'Guardando…' : 'Guardar regla'}
                            </Button>
                        </ButtonContainer>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
};

const mapStateToProps = ({ auth, common }) => ({
    storeId: auth.storeId,
    storeData: common.storeData,
});

export default connect(mapStateToProps)(CartDiscountTierFormPage);
